import { z } from 'zod/v4';
import { CamelCasedPropertiesDeep } from 'type-fest';
import { createDefinitionSchema } from '../../utils/helpers';
import { unitOptionsSchema } from '../../utils';

/**
 * Type for the spec-driven `DashboardPanel` variant of the distribution chart.
 */
export interface DistributionChartSpec extends z.TypeOf<typeof distributionChartSpecSchema> {}

/**
 * Zod schema for the spec-driven `DashboardPanel` variant of the distribution chart.
 */
export const distributionChartSpecSchema = z.object({
  unit: unitOptionsSchema.optional(),
  percentile_markers: z.array(z.number()).optional(),
});

/**
 * The schema for a DistributionChart `DashboardPanel` definition.
 */
export interface DistributionChartDefinition extends z.TypeOf<typeof distributionChartDefinitionSchema> {}
export const distributionChartDefinitionSchema = createDefinitionSchema(
  'DistributionChart',
  distributionChartSpecSchema
);

/**
 * Version of the Zod schema to be used for base types for the props-driven `Viz` variant of the distribution chart.
 *
 * Currently, it is the same as the spec-driven variant, but it may need to change in the future to
 * do things like flatten a specific property or some other transform (examples can be found in pie chart).
 */
export const distributionChartVizSchema = distributionChartSpecSchema;

/**
 * Base type for the props-driven `Viz` variant of the distribution chart generated from the Zod schema and
 * converting all snake_case properties to camelCase. This interface should be extended in `viz-framework`
 * to do the following:
 * - Add additional types for `Viz` only use cases.
 * - Extend properties to add more meaningful jsdoc. This is necessary because both `z.Typeof` and
 *   `CamelCasedPropertiesDeep` lose jsdoc.
 */
export interface DistributionChartPropsBase
  extends CamelCasedPropertiesDeep<z.TypeOf<typeof distributionChartVizSchema>> {}
